import { useDictation } from "@/hooks/useDictation";
import { useTranscriptPin } from "@/hooks/voice/useTranscriptPin";
import { cn } from "@/utils";
import { audioSettingsStorage } from "@/utils/audioSettings";
import { ArrowUp, Loader2, Mic, Square, X } from "lucide-react";
import { motion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";

type VoicePhase = "waiting" | "recording";

const COMPOSER_WIDTH = 320;

interface QuickVoiceComposerProps {
  position: { x: number; y: number };
  /** Called once transcription text is ready — parent switches to QuickChatComposer.
   *  Receives the composer's current screen position so dragging is respected. */
  onTranscriptionReady: (
    text: string,
    currentPos: { x: number; y: number },
  ) => void;
  /** Called when user hits send during/after recording — skips composer and sends immediately.
   *  Receives the composer's current screen position so dragging is respected. */
  onSend?: (text: string, currentPos: { x: number; y: number }) => void;
  onCancel: () => void;
}

function clampPos(x: number, y: number) {
  const APPROX_H = 160;
  return {
    x: Math.max(8, Math.min(x, window.innerWidth - COMPOSER_WIDTH - 8)),
    y: Math.max(
      8,
      Math.min(y - APPROX_H / 2, window.innerHeight - APPROX_H - 8),
    ),
  };
}

export function QuickVoiceComposer({
  position,
  onTranscriptionReady,
  onSend,
  onCancel,
}: QuickVoiceComposerProps) {
  const [phase, setPhase] = useState<VoicePhase>("waiting");

  // Pin the latest callbacks in refs so closures formed inside startRecording
  // (which may be stale by the time onstop fires) always call the current version.
  const onTranscriptionReadyRef = useRef(onTranscriptionReady);
  const onSendRef = useRef(onSend);
  const onCancelRef = useRef(onCancel);
  onTranscriptionReadyRef.current = onTranscriptionReady;
  onSendRef.current = onSend;
  onCancelRef.current = onCancel;

  // Set to true when the user hits send — transcription result goes straight to onSend.
  const sendOnTranscriptionRef = useRef(false);

  // Draggable position — posRef keeps async transcription callbacks in sync
  const [pos, setPos] = useState(() => clampPos(position.x, position.y));
  const posRef = useRef(pos);
  const dragState = useRef<{
    dragging: boolean;
    startX: number;
    startY: number;
    initX: number;
    initY: number;
  }>({ dragging: false, startX: 0, startY: 0, initX: 0, initY: 0 });

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      if (target.closest("button")) return;
      e.preventDefault();
      dragState.current = {
        dragging: true,
        startX: e.clientX,
        startY: e.clientY,
        initX: pos.x,
        initY: pos.y,
      };
      const APPROX_H = 160;
      const onMove = (ev: MouseEvent) => {
        if (!dragState.current.dragging) return;
        const next = {
          x: Math.max(
            8,
            Math.min(
              dragState.current.initX + ev.clientX - dragState.current.startX,
              window.innerWidth - COMPOSER_WIDTH - 8,
            ),
          ),
          y: Math.max(
            8,
            Math.min(
              dragState.current.initY + ev.clientY - dragState.current.startY,
              window.innerHeight - APPROX_H - 8,
            ),
          ),
        };
        posRef.current = next;
        setPos(next);
      };
      const onUp = () => {
        dragState.current.dragging = false;
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [pos],
  );

  const handleFinalText = useCallback((text: string) => {
    if (text.trim()) {
      if (sendOnTranscriptionRef.current) {
        sendOnTranscriptionRef.current = false;
        onSendRef.current?.(text, posRef.current);
      } else {
        onTranscriptionReadyRef.current(text, posRef.current);
      }
    } else {
      sendOnTranscriptionRef.current = false;
      toast.error("No transcription text received");
      onCancelRef.current();
    }
  }, []); // truly stable — all live values accessed via refs

  const {
    isRecording,
    audioLevel,
    duration,
    interimText,
    isFinalizing,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useDictation({
    onTranscriptionComplete: handleFinalText,
    onError: (err) => {
      sendOnTranscriptionRef.current = false;
      toast.error(err.message);
      onCancelRef.current();
    },
  });

  // Live transcript region: shows the most recent ~10 lines, pinned to the
  // newest text as it streams in (shared with the mobile voice screen).
  const {
    ref: transcriptRef,
    onScroll: handleTranscriptScroll,
    scrolled: transcriptScrolled,
  } = useTranscriptPin(interimText);

  // Auto-start on mount. Strict Mode runs mount → cleanup → mount, and the
  // cleanup genuinely cancels the first attempt (the facade does not fall
  // back on a cancelled connect), so the effect must be restartable rather
  // than start-once-guarded: each invocation starts its own attempt and a
  // cancelled one settles silently without touching this closure's state.
  useEffect(() => {
    let effectCancelled = false;

    const tryStart = async () => {
      if (!audioSettingsStorage.hasConfiguredDevice()) {
        // Keyed so Strict Mode's double invocation shows it once.
        toast("Using default microphone.", {
          icon: "🎤",
          duration: 2000,
          id: "quick-voice-default-mic",
        });
      }
      try {
        await startRecording();
        if (!effectCancelled) {
          setPhase("recording");
        }
      } catch {
        if (effectCancelled) return;
        toast.error("Could not access microphone.");
        onCancelRef.current();
      }
    };
    void tryStart();

    return () => {
      effectCancelled = true;
      // Abandon any active session so we don't leave a zombie recording if
      // the component unmounts early. A no-op when the dictation already
      // completed.
      cancelRecording();
    };
    // startRecording and cancelRecording are from useDictation; this effect
    // intentionally runs once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStop = useCallback(() => {
    if (isRecording) stopRecording();
  }, [isRecording, stopRecording]);

  const handleSendWhileRecording = useCallback(() => {
    sendOnTranscriptionRef.current = true;
    if (isRecording) stopRecording();
  }, [isRecording, stopRecording]);

  const handleCancel = useCallback(() => {
    cancelRecording();
    onCancelRef.current();
  }, [cancelRecording]);

  // Escape to cancel, Space/Enter to stop recording.
  // Space and Enter are always prevented while the composer is mounted to stop
  // keypresses from activating any DOM element that had focus before the composer
  // opened (e.g. an "Upload Files" button that would open the file picker).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCancel();
        return;
      }
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        if (phase === "recording") handleStop();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [phase, handleCancel, handleStop]);

  // Waveform bars
  const bars = Array.from({ length: 5 });
  const barHeights = bars.map((_, i) =>
    Math.max(4, Math.round(audioLevel * 28 * (Math.sin(i * 0.8) * 0.3 + 0.7))),
  );

  return createPortal(
    <motion.div
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.92 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
      className="fixed z-[10000] quick-voice-composer"
      style={{ left: pos.x, top: pos.y, width: COMPOSER_WIDTH }}
    >
      <div
        className={cn(
          "bg-neutral-0/95 backdrop-blur-xl border dark:border-neutral-700/50 rounded-2xl shadow-2xl shadow-black/20 dark:shadow-black/40",
          "flex flex-col overflow-hidden",
        )}
      >
        {/* Header — matches QuickChatComposer / ChatPanel exactly */}
        <div
          className="flex items-center justify-between px-3 py-2.5 border-b dark:border-neutral-800/60 cursor-grab active:cursor-grabbing select-none shrink-0"
          onMouseDown={handleDragStart}
        >
          <div className="flex items-center gap-2.5">
            <Mic className="h-4 w-4 text-foreground" strokeWidth={2} />
            <span className="text-sm font-semibold text-foreground">
              Quick Chat
            </span>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent dark:hover:bg-n-95/10 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex items-center justify-between px-3.5 py-3 gap-3">
          {isFinalizing ? (
            <div className="flex items-center gap-2 text-sm text-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0" />
              <span>Transcribing…</span>
            </div>
          ) : (
            <>
              {/* Left: stop button */}
              <button
                onClick={handleStop}
                disabled={phase !== "recording"}
                className={cn(
                  "h-7 w-7 flex items-center justify-center rounded-full shrink-0",
                  "transition-all duration-150",
                  phase === "recording"
                    ? "bg-lc-blocked/20 text-lc-blocked hover:bg-lc-blocked/30 cursor-pointer"
                    : "text-muted-foreground cursor-default",
                )}
              >
                <Square className="h-3 w-3 fill-current" />
              </button>

              {/* Center: timer + waveform */}
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="font-mono tabular-nums text-sm text-foreground shrink-0">
                  {String(Math.floor(duration / 60)).padStart(2, "0")}:
                  {String(duration % 60).padStart(2, "0")}
                </span>
                {phase === "waiting" ? (
                  <span className="text-xs text-muted-foreground">
                    Starting…
                  </span>
                ) : (
                  <div className="flex items-center gap-0.5 h-5 flex-1 justify-center">
                    {bars.map((_, i) => (
                      <motion.div
                        key={i}
                        className="w-1 rounded-full bg-foreground/70"
                        animate={{
                          height: phase === "recording" ? barHeights[i] : 3,
                        }}
                        transition={{ duration: 0.08, ease: "easeOut" }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Right: send button — stops recording and sends transcription immediately */}
              <button
                onClick={handleSendWhileRecording}
                disabled={phase !== "recording"}
                className={cn(
                  "h-7 w-7 flex items-center justify-center rounded-full shrink-0",
                  "transition-all duration-150",
                  phase === "recording"
                    ? "bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
                    : "border text-muted-foreground cursor-default",
                )}
              >
                <ArrowUp className="h-3.5 w-3.5" strokeWidth={2.5} />
              </button>
            </>
          )}
        </div>

        {/* Live transcript — streams in word-by-word while dictating. Capped
            at ~10 lines (15em at leading-normal), pinned to the newest text;
            the top fade appears once earlier lines have scrolled away. */}
        {interimText && (
          <div className="px-3.5 pb-3 -mt-1">
            <div
              ref={transcriptRef}
              onScroll={handleTranscriptScroll}
              className={cn(
                "max-h-[15em] overflow-y-auto overscroll-contain no-scrollbar",
                "text-sm leading-normal text-foreground/80 break-words",
                transcriptScrolled &&
                  "[mask-image:linear-gradient(to_bottom,transparent,black_1.5em)]",
              )}
            >
              {interimText}
            </div>
          </div>
        )}
      </div>
    </motion.div>,
    document.body,
  );
}
